// In-game calculator engine: lexer + Pratt parser + evaluator over typed
// quantities. Pure ESM with zero imports — the same file runs under webpack
// (Vue components) and plain `node --test` (see __tests__/), which is why
// everything the engine needs from the game (resources, tick rate, now) is
// injected through an env object built in env.js.
//
// Quantity model — all internal values are canonical:
//   scalar   { k:'scalar', v, res }        res: 'credit'|'technology'|'ideology'|null
//   rate     { k:'rate', vPerHour, res }   per REAL hour
//   dur      { k:'dur', s }                real seconds
//   dt       { k:'dt', ms }                epoch milliseconds
// plus statement-level results ('eta', 'afford', 'snapshot') that only the
// formatter consumes. Unit propagation happens in applyBin: amount ÷ rate
// gives a duration, rate × duration gives an amount, mixing two different
// resources is an error, a bare number unifies with anything.

export class CalcError extends Error {
  constructor(code, data = {}) {
    super(code);
    this.code = code;
    this.data = data;
  }
}

const err = (code, data) => { throw new CalcError(code, data); };

// ---------------------------------------------------------------------------
// Word tables
// ---------------------------------------------------------------------------

const RESOURCE_WORDS = {
  c: 'credit',
  credit: 'credit',
  credits: 'credit',
  t: 'technology',
  tech: 'technology',
  technology: 'technology',
  i: 'ideology',
  ideo: 'ideology',
  ideology: 'ideology',
};

// Multi-word names are merged by the lexer before classification (longest
// match first), so 'credit income' arrives here as a single key.
const INCOME_WORDS = {
  ci: 'credit',
  'credit income': 'credit',
  'credits income': 'credit',
  ti: 'technology',
  'tech income': 'technology',
  'technology income': 'technology',
  ii: 'ideology',
  'ideo income': 'ideology',
  'ideology income': 'ideology',
};

const LEXSLOT_WORDS = ['lex slot', 'lex slot cost'];

// seconds per unit; 'tick' is resolved against env.perHour at eval time.
const DURATION_UNITS = {
  s: 1, sec: 1, secs: 1, second: 1, seconds: 1,
  m: 60, min: 60, mins: 60, minute: 60, minutes: 60,
  h: 3600, hr: 3600, hrs: 3600, hour: 3600, hours: 3600,
  d: 86400, day: 86400, days: 86400,
  tick: 'tick', ticks: 'tick', ut: 'tick', uts: 'tick',
};

const KEYWORDS = ['until', 'in', 'at', 'afford', 'today', 'tomorrow'];

// JS Date#getDay() numbering (sunday = 0).
const WEEKDAYS = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
};

const MULTIWORDS = [...LEXSLOT_WORDS, ...Object.keys(INCOME_WORDS)]
  .filter((w) => w.includes(' '))
  .map((w) => w.split(' '));

// ---------------------------------------------------------------------------
// Lexer
// ---------------------------------------------------------------------------

// A resource word (not the start of '<res> income') right after a number.
const MILLIONS_RES_LOOKAHEAD = new RegExp(
  `^\\s*(?:${Object.keys(RESOURCE_WORDS).join('|')})(?![\\w])(?!\\s+income\\b)`,
  'i',
);

const OPS = {
  '+': '+', '-': '-', '−': '-', '*': '*', '×': '*', x: null, // 'x' stays a word
  '/': '/', '÷': '/', '(': '(', ')': ')', '=': '=', '%': '%',
};

export function tokenize(src) {
  const tokens = [];
  const s = src;
  let p = 0;

  const words = []; // pending word run, merged for multi-word names

  const flushWords = () => {
    let idx = 0;
    while (idx < words.length) {
      let merged = null;
      // longest multi-word match first (3 then 2 words)
      for (const len of [3, 2]) {
        if (idx + len > words.length) continue;
        const joined = words.slice(idx, idx + len).map((w) => w.text).join(' ');
        if (MULTIWORDS.some((mw) => mw.join(' ') === joined)) {
          merged = { text: joined, len };
          break;
        }
      }
      const w = merged || { text: words[idx].text, len: 1 };
      tokens.push(classifyWord(w.text, words[idx].pos));
      idx += w.len;
    }
    words.length = 0;
  };

  while (p < s.length) {
    const ch = s[p];

    if (/\s/.test(ch)) { p += 1; continue; }

    // time of day: 18:00 / 7:30
    const todMatch = /^(\d{1,2}):(\d{2})(?!\d)/.exec(s.slice(p));
    if (todMatch) {
      flushWords();
      tokens.push({ t: 'timeofday', h: parseInt(todMatch[1], 10), m: parseInt(todMatch[2], 10), pos: p });
      p += todMatch[0].length;
      continue;
    }

    // Magnitude suffixes: k/K = thousands, uppercase M = millions, always.
    // Lowercase m is also the minutes unit (in 30m), so it only means
    // millions when a resource word follows (1.5m tech) AND no in/at
    // keyword came earlier in the line (in 30m i check… stays minutes).
    const numMatch = /^(\d+(?:\.\d+)?)([kKM]?)(?![\w.])/.exec(s.slice(p));
    if (numMatch) {
      flushWords();
      const mult = { k: 1000, K: 1000, M: 1e6 }[numMatch[2]] || 1;
      tokens.push({ t: 'num', v: parseFloat(numMatch[1]) * mult, pos: p });
      p += numMatch[0].length;
      continue;
    }
    const millMatch = /^(\d+(?:\.\d+)?)m(?![\w.])/.exec(s.slice(p));
    if (millMatch) {
      flushWords();
      const afterRes = MILLIONS_RES_LOOKAHEAD.test(s.slice(p + millMatch[0].length));
      const inTimeClause = tokens.some((tok) => tok.t === 'kw' && (tok.kw === 'in' || tok.kw === 'at'));
      if (afterRes && !inTimeClause) {
        tokens.push({ t: 'num', v: parseFloat(millMatch[1]) * 1e6, pos: p, lowerM: p + millMatch[0].length - 1 });
        p += millMatch[0].length;
        continue;
      }
    }
    // number directly followed by a word (4h, 70t, 9800ideo): lex digits only
    const numPrefix = /^(\d+(?:\.\d+)?)/.exec(s.slice(p));
    if (numPrefix) {
      flushWords();
      tokens.push({ t: 'num', v: parseFloat(numPrefix[1]), pos: p });
      p += numPrefix[0].length;
      continue;
    }

    if (Object.prototype.hasOwnProperty.call(OPS, ch) && OPS[ch]) {
      flushWords();
      tokens.push({ t: OPS[ch], pos: p });
      p += 1;
      continue;
    }

    const wordMatch = /^[a-zA-ZÀ-ɏ_][a-zA-ZÀ-ɏ0-9_]*/.exec(s.slice(p));
    if (wordMatch) {
      words.push({ text: wordMatch[0].toLowerCase(), pos: p });
      p += wordMatch[0].length;
      continue;
    }

    // Unknown characters become junk tokens instead of hard errors so
    // free-text note labels ("don't forget!") survive tokenization; a
    // junk token inside an actual expression still fails at parse.
    flushWords();
    tokens.push({ t: 'junk', pos: p });
    p += 1;
  }

  flushWords();
  tokens.push({ t: 'eof', pos: p });
  return tokens;
}

function classifyWord(text, pos) {
  if (KEYWORDS.includes(text)) return { t: 'kw', kw: text, pos };
  if (Object.prototype.hasOwnProperty.call(WEEKDAYS, text)) return { t: 'weekday', dow: WEEKDAYS[text], pos };
  if (LEXSLOT_WORDS.includes(text)) return { t: 'lexslot', pos };
  if (Object.prototype.hasOwnProperty.call(INCOME_WORDS, text)) return { t: 'income', res: INCOME_WORDS[text], pos };
  if (Object.prototype.hasOwnProperty.call(DURATION_UNITS, text)) {
    return { t: 'unit', factor: DURATION_UNITS[text], pos, word: text };
  }
  if (Object.prototype.hasOwnProperty.call(RESOURCE_WORDS, text)) {
    return { t: 'resword', res: RESOURCE_WORDS[text], pos, word: text };
  }
  return { t: 'word', word: text, pos };
}

// ---------------------------------------------------------------------------
// Parser (Pratt for arithmetic, hand-rolled statement forms)
// ---------------------------------------------------------------------------

class Parser {
  constructor(tokens, srcText) {
    this.tokens = tokens;
    this.srcText = srcText || '';
    this.p = 0;
  }

  peek(offset = 0) { return this.tokens[this.p + offset]; }

  next() { const tok = this.tokens[this.p]; this.p += 1; return tok; }

  expectEof() {
    if (this.peek().t !== 'eof') err('PARSE', { at: this.peek().pos });
  }

  // statement := word '=' expr
  //            | 'until' ['+'] expr [note] | 'afford' expr [note]
  //            | ['in' durexpr | 'at' when] [note]  (bare: projection or note reminder)
  //            | expr ['in' durexpr | 'at' when] [note]
  // A note is any trailing free text after a complete verb form — it
  // labels the line and (for bare in/at) turns it into a note reminder.
  parseStatement() {
    const first = this.peek();

    // assigning to a reserved word (h = 5, credits = 1) deserves a
    // specific error, not a generic parse failure
    if (this.peek(1).t === '=' && first.t !== 'word' && first.t !== 'eof') {
      err('NAME_TAKEN', { name: first.word || first.kw || first.t });
    }

    if (first.t === 'word' && this.peek(1).t === '=') {
      this.next(); this.next();
      const e = this.parseTail(this.parseExpr());
      this.expectEof();
      return { t: 'assign', name: first.word, e };
    }

    if (first.t === 'kw' && (first.kw === 'until' || first.kw === 'afford')) {
      this.next();
      // until +5000 c: target relative to the stockpile at line creation
      let relative = false;
      if (first.kw === 'until' && this.peek().t === '+') {
        this.next();
        relative = true;
      }
      const e = this.parseExpr();
      const note = this.captureNote();
      this.expectEof();
      return { t: first.kw, e, relative, note };
    }

    if (first.t === 'kw' && first.kw === 'in') {
      this.next();
      const d = this.parseExpr();
      const note = this.captureNote();
      this.expectEof();
      return { t: 'in', e: null, d, note };
    }
    if ((first.t === 'kw' && (first.kw === 'at' || first.kw === 'today' || first.kw === 'tomorrow'))
      || first.t === 'weekday' || first.t === 'timeofday') {
      if (first.kw === 'at') this.next();
      const when = this.parseWhen();
      const note = this.captureNote();
      this.expectEof();
      return { t: 'at', e: null, when, note };
    }

    const e = this.parseTail(this.parseExpr());
    if (e.t === 'in' || e.t === 'at') {
      e.note = this.captureNote();
    }
    this.expectEof();
    return e;
  }

  // Swallow everything from the current token to eof as a note label.
  // Returns the raw source substring (leading punctuation stripped),
  // or null when the statement ended cleanly.
  captureNote() {
    const tok = this.peek();
    if (tok.t === 'eof') return null;
    this.p = this.tokens.length - 1;
    const text = this.srcText.slice(tok.pos).trim().replace(/^[:\-–—,.]\s*/, '');
    return text || null;
  }

  // optional 'in <durexpr>' / 'at <when>' suffix after an expression
  parseTail(e) {
    const tok = this.peek();
    if (tok.t === 'kw' && tok.kw === 'in') {
      this.next();
      return { t: 'in', e, d: this.parseExpr() };
    }
    if (tok.t === 'kw' && tok.kw === 'at') {
      this.next();
      return { t: 'at', e, when: this.parseWhen() };
    }
    return e;
  }

  // when := '+' durexpr | timeofday | weekday [timeofday]
  //       | ('today'|'tomorrow') [timeofday]
  parseWhen() {
    const tok = this.next();
    let base = null;
    if (tok.t === '+') return { t: 'when', plus: this.parseExpr() }; // at +2h …
    if (tok.t === 'timeofday') return { t: 'when', day: null, tod: { h: tok.h, m: tok.m } };
    if (tok.t === 'weekday') base = { t: 'when', day: { dow: tok.dow } };
    else if (tok.t === 'kw' && (tok.kw === 'today' || tok.kw === 'tomorrow')) base = { t: 'when', day: { rel: tok.kw } };
    else err('PARSE', { at: tok.pos });

    const nxt = this.peek();
    if (nxt.t === 'timeofday') {
      this.next();
      base.tod = { h: nxt.h, m: nxt.m };
    }
    return base;
  }

  parseExpr(minPrec = 0) {
    let left = this.parseUnary();

    for (;;) {
      const tok = this.peek();
      const prec = tok.t === '+' || tok.t === '-' ? 1 : (tok.t === '*' || tok.t === '/' ? 2 : -1);
      if (prec < 0 || prec < minPrec) return left;
      this.next();

      // rate literal: expr / <unit>  (70/tick, 500/day, x/h)
      if (tok.t === '/' && this.peek().t === 'unit') {
        const unit = this.next();
        left = { t: 'per', e: left, factor: unit.factor };
        continue;
      }

      const right = this.parseExpr(prec + 1);
      left = { t: 'bin', op: tok.t, l: left, r: right };
    }
  }

  parseUnary() {
    const tok = this.peek();
    if (tok.t === '-') {
      this.next();
      return { t: 'neg', e: this.parseUnary() };
    }
    if (tok.t === '+') {
      this.next();
      return this.parseUnary();
    }
    return this.parsePrimary();
  }

  parsePrimary() {
    const tok = this.next();

    if (tok.t === 'num') {
      const nxt = this.peek();
      if (nxt.t === 'unit') { // duration literal: 4h, 30 min, 12 ticks
        this.next();
        return { t: 'dur', v: tok.v, factor: nxt.factor };
      }
      if (nxt.t === 'resword') { // amount literal: 9800 ideo
        this.next();
        const amt = { t: 'amt', v: tok.v, res: nxt.res };
        // rate literal with resource: 70 ideo/tick
        if (this.peek().t === '/' && this.peek(1).t === 'unit') {
          this.next();
          const unit = this.next();
          return { t: 'per', e: amt, factor: unit.factor };
        }
        return amt;
      }
      if (nxt.t === '%') {
        this.next();
        return { t: 'num', v: tok.v / 100 };
      }
      // rate literal: 70/tick, 500/day — must bind tighter than plain
      // division so `8400 / 70/tick` reads as 8400 ÷ (70 per tick).
      if (nxt.t === '/' && this.peek(1).t === 'unit') {
        this.next();
        const unit = this.next();
        return { t: 'per', e: { t: 'num', v: tok.v }, factor: unit.factor };
      }
      return { t: 'num', v: tok.v };
    }

    if (tok.t === 'resword') return { t: 'stock', res: tok.res };
    if (tok.t === 'income') return { t: 'income', res: tok.res };
    if (tok.t === 'lexslot') return { t: 'lexslot' };
    if (tok.t === 'word') return { t: 'name', name: tok.word };
    if (tok.t === 'unit') return { t: 'unitref', factor: tok.factor }; // bare 'h' in 'x/h' handled by 'per'; alone → error at eval

    if (tok.t === '(') {
      const e = this.parseExpr();
      if (this.next().t !== ')') err('PARSE', { at: tok.pos });
      return e;
    }

    err('PARSE', { at: tok.pos });
    return null;
  }
}

export function parse(src) {
  return new Parser(tokenize(src), src).parseStatement();
}

// ---------------------------------------------------------------------------
// Canonicalization: loose phrasing -> calculator syntax
// ---------------------------------------------------------------------------
//
// Players ask questions ("when will I have 30k tech?", "30k tech?", "u 15k t")
// rather than write syntax. canonicalize() rewrites those into the canonical
// line the parser understands; evaluation always runs on the canonical form
// and the UI shows it whenever it differs from what was typed. Rules, in order:
//
//   1. A trailing '?' is dropped — unless it belongs to a real note label
//      ("in 2h did the ship land?" keeps it).
//   2. A leading question phrase / verb alias is replaced by its verb:
//      when|u|till|how long until [will|do|can I have|get|reach] -> until,
//      [when] can|could I afford -> afford. The phrase words (including
//      "I") are only dropped as part of a whole phrase, so a standalone
//      'i' after a number stays ideology. Because u/when/till are not
//      reserved, the alias only applies when the line doesn't already
//      parse on its own (u = 5 keeps defining a name).
//   3. A line that is exactly one amount literal ("345k tech") becomes
//      "until 345k tech" — arithmetic ("9800 ideo + 3600") is untouched.
//   4. Lowercase-m millions are spelled 'M' so stored lines are unambiguous.
//
// A rewrite whose result doesn't parse is abandoned (the original text
// then fails with its usual error). canonicalize is idempotent.

const SUBJ = '(?:i|we)';
const HAVE = '(?:have|get|reach|hit|own)';
const AUX = '(?:will|would|do|can|could|shall)';
const QUESTION_PHRASES = [
  // afford forms first (they would otherwise match the generic 'when')
  [new RegExp(`^(?:when|how soon|how long (?:until|till|til|before))\\s+${AUX}\\s+${SUBJ}\\s+(?:be able to\\s+)?afford\\s+`, 'i'), 'afford'],
  [new RegExp(`^when\\s+${SUBJ}(?:'|’)?ll\\s+(?:be able to\\s+)?afford\\s+`, 'i'), 'afford'],
  [new RegExp(`^(?:can|could)\\s+${SUBJ}\\s+(?:already\\s+)?afford\\s+`, 'i'), 'afford'],
  // until forms
  [new RegExp(`^(?:when|how soon|how long (?:until|till|til|before))\\s+${AUX}\\s+${SUBJ}\\s+${HAVE}\\s+`, 'i'), 'until'],
  [new RegExp(`^when\\s+${SUBJ}(?:'|’)?ll\\s+${HAVE}\\s+`, 'i'), 'until'],
  [new RegExp(`^when(?:'|’)ll\\s+${SUBJ}\\s+${HAVE}\\s+`, 'i'), 'until'],
  [new RegExp(`^how long (?:until|till|til|before|to)\\s+(?:${SUBJ}\\s+${HAVE}\\s+)?`, 'i'), 'until'],
  [new RegExp(`^(?:when|until|till|til|u)\\s+(?:${SUBJ}\\s+${HAVE}\\s+)?`, 'i'), 'until'],
];

// Verb aliases the UI treats as complete words (Tab-completion logic).
const VERB_ALIASES = ['u', 'when', 'till', 'til'];

const tryParse = (src) => {
  try {
    return parse(src);
  } catch (e) {
    return null;
  }
};

const collapse = (s) => s.replace(/\s+/g, ' ').trim();

export function canonicalize(src) {
  const original = collapse(String(src || ''));
  let line = original;

  // 1. trailing question marks
  if (/\?\s*$/.test(line)) {
    const ast = tryParse(line);
    const noteOwnsMark = ast && ast.note && !/^[?\s]+$/.test(ast.note);
    if (!noteOwnsMark) line = line.replace(/[\s?]+$/, '');
  }
  if (!line) return original;

  // 2. question phrases / verb aliases
  let verbed = false;
  for (const [re, verb] of QUESTION_PHRASES) {
    const m = re.exec(line);
    if (!m) continue;
    const rest = line.slice(m[0].length);
    if (!rest) break;
    const candidate = `${verb} ${rest}`;
    if (candidate === line) { verbed = true; break; }
    // a lone alias word may be a user name (u = 5, u + 1): only rewrite
    // when the line means nothing as typed
    // ('u +5000 c' — '+' glued to the number — is still the relative form)
    const loneAlias = !/\s/.test(m[0].trim()) && !/^\+\d/.test(rest);
    if ((loneAlias && tryParse(line)) || !tryParse(candidate)) break;
    line = candidate;
    verbed = true;
    break;
  }

  // 3. a bare amount literal asks "when will I have it"
  if (!verbed) {
    let toks = null;
    try { toks = tokenize(line); } catch (e) { toks = null; }
    if (toks && toks.length === 3 && toks[0].t === 'num' && toks[1].t === 'resword') {
      line = `until ${line}`;
    }
  }

  // 4. spell lowercase-m millions as M
  try {
    const marks = tokenize(line).filter((tok) => tok.lowerM != null).map((tok) => tok.lowerM);
    marks.reverse().forEach((at) => { line = `${line.slice(0, at)}M${line.slice(at + 1)}`; });
  } catch (e) {
    // tokenize never throws today; stay defensive
  }

  if (line !== original && !tryParse(line)) return original;
  return line;
}

// { text, changed } — changed is true when the canonical line differs from
// what was typed (whitespace aside), i.e. when the UI should show it.
export function interpret(src) {
  const text = canonicalize(src);
  return { text, changed: text !== collapse(String(src || '')) };
}

// Is `word` a complete calculator word (keyword, resource, unit, alias…)?
export function isKnownWord(word) {
  const w = String(word || '').toLowerCase();
  return isReservedName(w) || VERB_ALIASES.includes(w);
}

// ---------------------------------------------------------------------------
// Quantity algebra
// ---------------------------------------------------------------------------

const scalar = (v, res = null) => ({ k: 'scalar', v, res });
const rate = (vPerHour, res = null) => ({ k: 'rate', vPerHour, res });
const dur = (s) => ({ k: 'dur', s });

function unifyRes(a, b) {
  if (a && b && a !== b) err('MIXED_RESOURCES', { a, b });
  return a || b;
}

function applyBin(op, l, r) {
  if (op === '+' || op === '-') {
    const sign = op === '+' ? 1 : -1;
    if (l.k === 'scalar' && r.k === 'scalar') return scalar(l.v + sign * r.v, unifyRes(l.res, r.res));
    if (l.k === 'rate' && r.k === 'rate') return rate(l.vPerHour + sign * r.vPerHour, unifyRes(l.res, r.res));
    if (l.k === 'dur' && r.k === 'dur') return dur(l.s + sign * r.s);
    if (l.k === 'dt' && r.k === 'dur') return { k: 'dt', ms: l.ms + sign * r.s * 1000 };
    if (l.k === 'dur' && r.k === 'dt' && op === '+') return { k: 'dt', ms: r.ms + l.s * 1000 };
    if (l.k === 'dt' && r.k === 'dt' && op === '-') return dur((l.ms - r.ms) / 1000);
    err('BAD_OP', { op, l: l.k, r: r.k });
  }

  if (op === '*') {
    if (l.k === 'scalar' && r.k === 'scalar') {
      if (l.res && r.res) err('BAD_OP', { op, l: l.res, r: r.res });
      return scalar(l.v * r.v, l.res || r.res);
    }
    if (l.k === 'scalar' && r.k === 'rate') {
      if (l.res) err('BAD_OP', { op, l: l.res, r: 'rate' });
      return rate(l.v * r.vPerHour, r.res);
    }
    if (l.k === 'rate' && r.k === 'scalar') return applyBin('*', r, l);
    if (l.k === 'rate' && r.k === 'dur') return scalar(l.vPerHour * (r.s / 3600), l.res);
    if (l.k === 'dur' && r.k === 'rate') return applyBin('*', r, l);
    if (l.k === 'scalar' && r.k === 'dur') {
      if (l.res) err('BAD_OP', { op, l: l.res, r: 'duration' });
      return dur(l.v * r.s);
    }
    if (l.k === 'dur' && r.k === 'scalar') return applyBin('*', r, l);
    err('BAD_OP', { op, l: l.k, r: r.k });
  }

  if (op === '/') {
    if (l.k === 'scalar' && r.k === 'scalar') {
      if (r.v === 0) err('DIV_ZERO');
      if (!r.res) return scalar(l.v / r.v, l.res);
      unifyRes(l.res, r.res);
      return scalar(l.v / r.v, null);
    }
    if (l.k === 'scalar' && r.k === 'rate') { // the ETA workhorse
      unifyRes(l.res, r.res);
      if (r.vPerHour === 0) err('DIV_ZERO');
      return dur((l.v / r.vPerHour) * 3600);
    }
    if (l.k === 'scalar' && r.k === 'dur') {
      if (r.s === 0) err('DIV_ZERO');
      return rate(l.v / (r.s / 3600), l.res);
    }
    if (l.k === 'rate' && r.k === 'scalar') {
      if (r.res) err('BAD_OP', { op, l: 'rate', r: r.res });
      if (r.v === 0) err('DIV_ZERO');
      return rate(l.vPerHour / r.v, l.res);
    }
    if (l.k === 'rate' && r.k === 'rate') {
      unifyRes(l.res, r.res);
      if (r.vPerHour === 0) err('DIV_ZERO');
      return scalar(l.vPerHour / r.vPerHour, null);
    }
    if (l.k === 'dur' && r.k === 'scalar') {
      if (r.res) err('BAD_OP', { op, l: 'duration', r: r.res });
      if (r.v === 0) err('DIV_ZERO');
      return dur(l.s / r.v);
    }
    if (l.k === 'dur' && r.k === 'dur') {
      if (r.s === 0) err('DIV_ZERO');
      return scalar(l.s / r.s, null);
    }
    err('BAD_OP', { op, l: l.k, r: r.k });
  }

  err('BAD_OP', { op, l: l.k, r: r.k });
  return null;
}

// ---------------------------------------------------------------------------
// Evaluator
// ---------------------------------------------------------------------------

const tickSeconds = (env) => 3600 / env.perHour;

function incomePerHour(env, res) {
  return env.resources[res].changePerUt * env.perHour;
}

// Resolve a when-clause to epoch ms. `anchor` is the reference "now":
// projections anchor to the live clock, note reminders anchor to the
// line's creation time so "at fri 18:00 move navarch" stays THAT Friday
// forever. allowPast lets an elapsed note read as overdue instead of
// erroring.
function whenToMs(when, env, { anchor, allowPast } = {}) {
  const ref = anchor != null ? anchor : env.now;

  if (when.plus) { // at +2h …
    const d = evalNode(when.plus, env);
    if (d.k !== 'dur') err('BAD_OP', { op: 'at+', r: d.k });
    return ref + d.s * 1000;
  }

  const now = new Date(ref);
  const target = new Date(ref);
  target.setSeconds(0, 0);

  const tod = when.tod || { h: 0, m: 0 };
  target.setHours(tod.h, tod.m, 0, 0);

  if (when.day && typeof when.day.dow === 'number') {
    let delta = (when.day.dow - now.getDay() + 7) % 7;
    if (delta === 0 && target.getTime() <= ref) delta = 7;
    target.setDate(target.getDate() + delta);
  } else if (when.day && when.day.rel === 'tomorrow') {
    target.setDate(target.getDate() + 1);
  } else if (when.day && when.day.rel === 'today') {
    // as-is
  } else if (!when.day) {
    // bare time of day: next occurrence
    if (target.getTime() <= ref) target.setDate(target.getDate() + 1);
  }

  if (!allowPast && target.getTime() <= env.now) err('PAST_TIME');
  return target.getTime();
}

function projectResource(env, res, seconds) {
  return env.resources[res].value + incomePerHour(env, res) * (seconds / 3600);
}

function evalNode(node, env) {
  switch (node.t) {
    case 'num': return scalar(node.v, null);
    case 'amt': return scalar(node.v, node.res);
    case 'dur': {
      const factor = node.factor === 'tick' ? tickSeconds(env) : node.factor;
      return dur(node.v * factor);
    }
    case 'stock': return scalar(env.resources[node.res].value, node.res);
    case 'income': return rate(incomePerHour(env, node.res), node.res);
    case 'lexslot': {
      if (env.lexSlotCost == null) err('NO_DATA', { what: 'lex slot' });
      return scalar(env.lexSlotCost, 'ideology');
    }
    case 'name': {
      if (!env.names.has(node.name)) err('UNKNOWN_NAME', { name: node.name });
      return env.names.get(node.name);
    }
    case 'unitref': err('PARSE', {});
      return null;
    case 'neg': {
      const v = evalNode(node.e, env);
      if (v.k === 'scalar') return scalar(-v.v, v.res);
      if (v.k === 'rate') return rate(-v.vPerHour, v.res);
      if (v.k === 'dur') return dur(-v.s);
      err('BAD_OP', { op: '-', l: v.k });
      return null;
    }
    case 'per': {
      const v = evalNode(node.e, env);
      if (v.k !== 'scalar') err('BAD_OP', { op: '/unit', l: v.k });
      const factor = node.factor === 'tick' ? tickSeconds(env) : node.factor;
      return rate(v.v / (factor / 3600), v.res);
    }
    case 'bin': return applyBin(node.op, evalNode(node.l, env), evalNode(node.r, env));

    case 'until': {
      const target = evalNode(node.e, env);
      if (target.k !== 'scalar') err('BAD_OP', { op: 'until', l: target.k });
      if (!target.res) err('NEED_RESOURCE');
      let targetV = target.v;
      if (node.relative) {
        // until +5000 c — relative to the stockpile when the line was
        // written (persisted per line), so the goalpost never moves.
        // Live previews have no snapshot yet and use the current value.
        const base = env.lineBase && typeof env.lineBase[target.res] === 'number'
          ? env.lineBase[target.res]
          : env.resources[target.res].value;
        targetV = base + target.v;
      }
      const label = node.note || null;
      const cur = env.resources[target.res].value;
      const need = targetV - cur;
      if (need <= 0) return { k: 'eta', res: target.res, target: targetV, label, reached: true, s: 0, when: env.now, paused: !env.isRunning };
      const rph = incomePerHour(env, target.res);
      if (rph <= 0) return { k: 'eta', res: target.res, target: targetV, label, never: true, need, paused: !env.isRunning };
      const s = (need / rph) * 3600;
      return { k: 'eta', res: target.res, target: targetV, label, need, s, when: env.now + s * 1000, paused: !env.isRunning };
    }

    case 'afford': {
      const cost = evalNode(node.e, env);
      if (cost.k !== 'scalar') err('BAD_OP', { op: 'afford', l: cost.k });
      if (!cost.res) err('NEED_RESOURCE');
      const label = node.note || null;
      const cur = env.resources[cost.res].value;
      if (cur >= cost.v) return { k: 'afford', res: cost.res, cost: cost.v, label, ok: true, paused: !env.isRunning };
      const shortfall = cost.v - cur;
      const rph = incomePerHour(env, cost.res);
      if (rph <= 0) return { k: 'afford', res: cost.res, cost: cost.v, label, ok: false, shortfall, never: true, paused: !env.isRunning };
      const s = (shortfall / rph) * 3600;
      return { k: 'afford', res: cost.res, cost: cost.v, label, ok: false, shortfall, s, when: env.now + s * 1000, paused: !env.isRunning };
    }

    case 'in':
    case 'at': {
      // A bare in/at with trailing text is a note reminder: its time is
      // anchored to the line's creation instant (env.lineTs), so
      // "in 2h colony ship arrives" means 2h from when it was written —
      // not a goalpost that slides forward with the clock.
      const isNote = node.e === null && node.note;
      const anchor = isNote ? (env.lineTs || env.now) : env.now;

      let seconds;
      let when;
      if (node.t === 'in') {
        const d = evalNode(node.d, env);
        if (d.k !== 'dur') err('BAD_OP', { op: 'in', r: d.k });
        when = anchor + d.s * 1000;
        seconds = (when - env.now) / 1000;
      } else {
        when = whenToMs(node.when, env, { anchor, allowPast: !!isNote });
        seconds = (when - env.now) / 1000;
      }

      if (isNote) {
        return { k: 'note', text: node.note, when, s: seconds, done: env.now >= when };
      }

      if (node.e === null) {
        const resources = {};
        ['credit', 'technology', 'ideology'].forEach((res) => {
          resources[res] = projectResource(env, res, seconds);
        });
        return { k: 'snapshot', s: seconds, when, resources, paused: !env.isRunning };
      }

      const base = evalNode(node.e, env);
      if (base.k !== 'scalar') err('BAD_OP', { op: node.t, l: base.k });
      if (!base.res) err('NEED_RESOURCE');
      const v = base.v + incomePerHour(env, base.res) * (seconds / 3600);
      return { k: 'projection', res: base.res, v, s: seconds, when, paused: !env.isRunning };
    }

    case 'assign': {
      if (isReservedName(node.name)) err('NAME_TAKEN', { name: node.name });
      const v = evalNode(node.e, env);
      env.names.set(node.name, v);
      return v;
    }

    default:
      err('PARSE', {});
      return null;
  }
}

function isReservedName(name) {
  return KEYWORDS.includes(name)
    || Object.prototype.hasOwnProperty.call(RESOURCE_WORDS, name)
    || Object.prototype.hasOwnProperty.call(INCOME_WORDS, name)
    || Object.prototype.hasOwnProperty.call(DURATION_UNITS, name)
    || Object.prototype.hasOwnProperty.call(WEEKDAYS, name)
    || LEXSLOT_WORDS.includes(name);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const parseCache = new Map();
const PARSE_CACHE_MAX = 300;

function parseCached(src) {
  if (parseCache.has(src)) return parseCache.get(src);
  const ast = parse(canonicalize(src));
  if (parseCache.size >= PARSE_CACHE_MAX) parseCache.clear();
  parseCache.set(src, ast);
  return ast;
}

// Evaluate one line inside an existing env (env.names is read AND written —
// assignments define names for subsequent lines).
export function evaluateLine(src, env) {
  const ast = parseCached(src);
  return evalNode(ast, env);
}

// Evaluate an ordered document of lines; returns one entry per line:
//   { ok: true, value } | { ok: false, error: CalcError }
// A failed line never poisons the rest of the document.
// Lines are either plain source strings or { src, base, ts } objects —
// base (resource stockpiles at creation) feeds `until +N` relative
// targets, ts (creation instant) anchors note reminders.
export function evaluateDoc(lines, env) {
  env.names = new Map(); // always a fresh namespace — deleted lines must not linger
  return lines.map((line) => {
    const src = typeof line === 'string' ? line : line.src;
    env.lineBase = (typeof line === 'object' && line.base) || null;
    env.lineTs = (typeof line === 'object' && line.ts) || null;
    try {
      return { ok: true, value: evaluateLine(src, env) };
    } catch (e) {
      if (e instanceof CalcError) return { ok: false, error: e };
      return { ok: false, error: new CalcError('PARSE', {}) };
    } finally {
      env.lineBase = null;
      env.lineTs = null;
    }
  });
}

// Autocomplete metadata for the UI. `insert` is the canonical text; labels
// are i18n ids resolved by the component (calc.suggest.<id>). `chip: false`
// keeps an alias out of the chips row (it would duplicate its verb).
export const COMPLETIONS = [
  { id: 'credit', insert: 'credit', kind: 'variable' },
  { id: 'tech', insert: 'tech', kind: 'variable' },
  { id: 'ideo', insert: 'ideo', kind: 'variable' },
  { id: 'credit_income', insert: 'credit income', kind: 'variable' },
  { id: 'tech_income', insert: 'tech income', kind: 'variable' },
  { id: 'ideo_income', insert: 'ideo income', kind: 'variable' },
  { id: 'lex_slot', insert: 'lex slot', kind: 'variable' },
  { id: 'until', insert: 'until ', kind: 'function' },
  { id: 'when', insert: 'when ', kind: 'function', chip: false },
  { id: 'in', insert: 'in ', kind: 'function' },
  { id: 'at', insert: 'at ', kind: 'function' },
  { id: 'afford', insert: 'afford ', kind: 'function' },
];
